import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { DollarSign, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Charges() {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCharges();
  }, []);

  const fetchCharges = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/charges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCharges(response.data);
    } catch (error) {
      toast.error('Erro ao carregar cobranças');
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = async (chargeId, platform) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/charges/${chargeId}/send-reminder?platform=${platform}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Lembrete enviado via ${platform}!`);
      fetchCharges();
    } catch (error) {
      toast.error('Erro ao enviar lembrete');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pendente: 'inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-xs font-semibold text-amber-600',
      pago: 'inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-600',
      atrasado: 'inline-flex items-center rounded-full border border-rose-500/30 bg-rose-500/10 px-2.5 py-0.5 text-xs font-semibold text-rose-600'
    };
    return badges[status] || badges.pendente;
  };

  return (
    <div className="flex min-h-screen bg-zinc-950" data-testid="charges-page">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Cobranças</h1>
          <p className="text-slate-400">Gerencie suas cobranças e lembretes</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-400">Carregando...</p>
          </div>
        ) : charges.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
            <DollarSign className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Nenhuma cobrança registrada ainda.</p>
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="charges-table">
                <thead className="bg-zinc-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Valor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Vencimento</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {charges.map((charge) => (
                    <tr key={charge.id} className="hover:bg-zinc-800/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{charge.client_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">R$ {charge.amount.toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                        {new Date(charge.due_date).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(charge.status)}>{charge.status}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => sendReminder(charge.id, 'whatsapp')}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            disabled={charge.sent_whatsapp}
                            data-testid={`send-whatsapp-${charge.id}`}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            {charge.sent_whatsapp ? 'Enviado' : 'WhatsApp'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => sendReminder(charge.id, 'telegram')}
                            className="bg-sky-600 hover:bg-sky-700"
                            disabled={charge.sent_telegram}
                            data-testid={`send-telegram-${charge.id}`}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            {charge.sent_telegram ? 'Enviado' : 'Telegram'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
